import React from 'react';
import { Link } from 'react-router-dom';
import { Card, ListGroup, Button } from 'react-bootstrap';
import AccessibleProgressBar from '@/components/common/AccessibleProgressBar';
import PropTypes from 'prop-types';
import '@/features/dashboard/Dashboard.css';

/**
 * Configuration for each profile completion step.
 * Keys correspond to the strings returned in profileCompletion.missing.
 */
const STEP_CONFIG = {
    name: {
        label: 'Add your name',
        description: 'Let coordinators and instructors know who you are.',
        link: '/profile/edit',
        linkText: 'Add Name',
    },
    department: {
        label: 'Set your department',
        description: 'Helps match you with relevant courses and programs.',
        link: '/profile/edit',
        linkText: 'Add Department',
    },
    major: {
        label: 'Add your major',
        description: 'Helps coordinators match you with the right positions.',
        link: '/profile/edit',
        linkText: 'Add Major',
    },
    phone: {
        label: 'Add a phone number',
        description: 'Coordinators may need to reach you during the semester.',
        link: '/profile/edit',
        linkText: 'Add Phone',
    },
    profilePicture: {
        label: 'Upload a profile photo',
        description: 'A photo helps others recognize you.',
        link: '/profile#profile-photo',
        linkText: 'Upload Photo',
    },
    studentId: {
        label: 'Add your student ID',
        description: 'Your 900-number is needed for employment processing.',
        link: '/profile/edit',
        linkText: 'Add ID',
    },
    tour: {
        label: 'Take the platform tour',
        description:
            'A quick walkthrough of courses, applications, assignments, and more.',
        action: 'tour',
        linkText: 'Start Tour',
    },
};

function getProgressVariant(percentage) {
    if (percentage < 40) return 'danger';
    if (percentage < 70) return 'warning';
    return 'success';
}

export default function ProfileCompletionCard({
    profileCompletion,
    onStartTour,
}) {
    if (!profileCompletion || profileCompletion.percentage >= 100) {
        return null;
    }

    const { percentage, missing } = profileCompletion;

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h2 className="h6 mb-0 fw-bold">Complete Your Profile</h2>
                    <span className="text-muted small">{percentage}%</span>
                </div>
                <AccessibleProgressBar
                    now={percentage}
                    variant={getProgressVariant(percentage)}
                    className="mb-3"
                    label={`Profile completion: ${percentage}%`}
                />
                <ListGroup variant="flush">
                    {missing.map((key) => {
                        const step = STEP_CONFIG[key];
                        if (!step) return null;

                        return (
                            <ListGroup.Item
                                key={key}
                                className="d-flex justify-content-between align-items-center px-0 border-start-0 border-end-0"
                            >
                                <div>
                                    <div className="small fw-medium">
                                        {step.label}
                                    </div>
                                    <div className="text-muted small">
                                        {step.description}
                                    </div>
                                </div>
                                {step.action === 'tour' ? (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill flex-shrink-0 ms-3 profile-step-action"
                                        onClick={onStartTour}
                                    >
                                        {step.linkText}
                                    </Button>
                                ) : (
                                    <Button
                                        as={Link}
                                        to={step.link}
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill flex-shrink-0 ms-3 profile-step-action"
                                    >
                                        {step.linkText}
                                    </Button>
                                )}
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
            </Card.Body>
        </Card>
    );
}

ProfileCompletionCard.propTypes = {
    profileCompletion: PropTypes.shape({
        percentage: PropTypes.number.isRequired,
        missing: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
    onStartTour: PropTypes.func.isRequired,
};
